<template>
    <div class="meeting-detail-view">
        <!-- ================= 1. 顶部：会议基础信息 ================= -->
        <div class="card-block header-card">
            <div>
                <div class="header-top">
                    <div class="title-section">
                        <div class="main-title">{{ detail.title }}</div>
                        <div class="tags">
                            <a-tag color="#ff4d4f" class="custom-tag">{{ findSingleDictName('meetingLevel', detail.type) || detail.type }}</a-tag>
                            <a-tag :color="detail.state === 'ended' ? '#d9d9d9' : '#1890ff'" class="custom-tag" :class="detail.state === 'ended' ? 'gray-tag' : ''">{{ findSingleDictName('meetingBookState', detail.state) || detail.state }}</a-tag>
                        </div>
                    </div>
                </div>
                <div class="header-info-row">
                <div class="info-item">
                    <a-icon type="clock-circle" /> {{ formatDate(detail.startTime) }} - {{ formatDate(detail.endTime) }}
                </div>
                <div class="info-item">
                    <a-icon type="environment" /> {{ detail.meetRoom ? detail.meetRoom.name + ' (' + detail.meetRoom.location + ')' : '' }}
                </div>
                <div class="info-item person-item">
                    <div class="avatar-circle" :style="{ background: getAvatarColor(hostInfo.userName) }">{{ hostInfo.userName ? hostInfo.userName.charAt(0) : '' }}</div>
                    <div class="person-detail">
                        <div class="person-name">{{ hostInfo.userName }}</div>
                        <div class="person-sub">
                            <span>{{ hostInfo.department }}</span>
                            <span>{{ hostInfo.role }}</span>
                            <span class="status-badge" :class="hostInfo.attendanceClass">{{ hostInfo.attendanceStatus }}</span>
                        </div>
                    </div>
                </div>
                <div class="info-item person-item">
                    <div class="avatar-circle" :style="{ background: getAvatarColor(recorderInfo.userName) }">{{ recorderInfo.userName ? recorderInfo.userName.charAt(0) : '' }}</div>
                    <div class="person-detail">
                        <div class="person-name">{{ recorderInfo.userName }}</div>
                        <div class="person-sub">
                            <span>{{ recorderInfo.department }}</span>
                            <span>{{ recorderInfo.role }}</span>
                            <span class="status-badge" :class="recorderInfo.attendanceClass">{{ recorderInfo.attendanceStatus }}</span>
                        </div>
                    </div>
                </div>
            </div>
            </div>
            <div class="title-actions">
                <a-button v-if="detail.state !== 'cancelled' && detail.state !== 'end' && canOperate" @click="handleOpenEdit">编辑会议</a-button>
                <a-button v-if="detail.state !== 'cancelled' && detail.state !== 'end' && canOperate" type="danger" ghost @click="handleCancelMeeting">取消会议</a-button>
            </div>
        </div>

        <!-- ================= 2. 会议内容 ================= -->
        <div class="card-block">
            <div class="section-title"><span class="bar"></span>会议内容</div>
            <div class="meeting-content-list">
                <div class="content-item">{{ detail.content }}</div>
            </div>
        </div>

        <!-- ================= 2.5 附件 ================= -->
        <div class="card-block" v-if="attachFileList.length">
            <div class="section-title"><span class="bar"></span>附件</div>
            <div class="file-list">
                <div class="file-item" v-for="(file, index) in attachFileList" :key="index">
                    <div class="file-info">
                        <a-icon :type="fileIcon(file.ext)" class="file-icon" />
                        <span class="file-name">{{ file.name }}</span>
                    </div>
                    <div class="file-actions">
                        <a-icon type="download" class="download-icon" @click="downloadFile(file)" />
                    </div>
                </div>
            </div>
        </div>

        <!-- ================= 3. 参会响应状态 ================= -->
        <div class="card-block">
            <div class="section-title"><span class="bar"></span>参会响应状态</div>

            <!-- 统计卡片 -->
            <div class="stats-grid">
                <div class="stat-card stat-green">
                    <div class="stat-num">{{ attendeeStats.confirmed }}</div>
                    <div class="stat-label">已确认</div>
                </div>
                <div class="stat-card stat-yellow">
                    <div class="stat-num">{{ attendeeStats.pending }}</div>
                    <div class="stat-label">待响应</div>
                </div>
                <div class="stat-card stat-red">
                    <div class="stat-num">{{ attendeeStats.leavePending }}</div>
                    <div class="stat-label">请假待审批</div>
                </div>
                <div class="stat-card stat-gray">
                    <div class="stat-num">{{ attendeeStats.leaveApproved }}</div>
                    <div class="stat-label">已请假</div>
                </div>
            </div>

            <!-- 状态过滤器 -->
            <div class="filter-tabs">
                <a-button :type="filterType === 'all' ? 'primary' : 'default'" size="small" @click="filterType='all'">全部</a-button>
                <a-button :type="filterType === 'confirmed' ? 'primary' : 'default'" size="small" @click="filterType='confirmed'">已确认</a-button>
                <a-button :type="filterType === 'pending' ? 'primary' : 'default'" size="small" @click="filterType='pending'">待响应</a-button>
                <a-button :type="filterType === 'leave' ? 'primary' : 'default'" size="small" @click="filterType='leave'">请假</a-button>
            </div>

            <!-- 人员列表 -->
            <div class="attendee-list-table">
                <a-table
                    :dataSource="filteredAttendeeList"
                    :pagination="false"
                    rowKey="id"
                    :columns="attendeeColumns"
                    size="middle"
                >
                    <!-- 姓名列 -->
                    <template slot="nameSlot" slot-scope="text, record">
                        <div class="user-cell">
                            <div class="avatar-circle small" :style="{ background: record.color }">{{ (text || '').charAt(0) }}</div>
                            <span>{{ text }}</span>
                        </div>
                    </template>

                    <!-- 响应状态列 (独立插槽) -->
                    <template slot="responseStatusSlot" slot-scope="text, record">
                        <span class="status-badge" :class="record.responseClass">{{ text }}</span>
                    </template>

                    <!-- 到会状态列 (独立插槽，防止样式混用) -->
                    <template slot="attendanceStatusSlot" slot-scope="text, record">
                        <span class="status-badge" :class="record.attendanceClass">{{ text }}</span>
                    </template>

                    <!-- 操作列 -->
                    <template slot="actionSlot" slot-scope="text, record">
                        <div v-if="record.needApproval && !currentUserIsRecorder" class="action-group">
                            <a-button type="primary" size="small" class="btn-approve" @click="handleApproveLeave(record)">批准</a-button>
                            <a-button type="default" size="small" class="btn-reject" @click="handleRejectLeave(record)">驳回</a-button>
                        </div>
                        <span v-else class="empty-action">-</span>
                    </template>
                </a-table>
            </div>
        </div>

        <!-- ================= 4. 会议纪要 ================= -->
        <div class="card-block" v-if="fileList.length">
            <div class="section-title"><span class="bar"></span>会议纪要</div>
            <div class="file-list">
                <div class="file-item" v-for="(file, index) in fileList" :key="index">
                    <div class="file-info">
                        <a-icon :type="fileIcon(file.ext)" class="file-icon" />
                        <span class="file-name">{{ file.name }}</span>
                    </div>
                    <div class="file-actions">
                        <a-icon type="download" class="download-icon" @click="downloadFile(file)" />
                    </div>
                </div>
            </div>
        </div>

        <!-- ================= 5. 督办记录 ================= -->
        <div class="card-block">
            <div class="section-title"><span class="bar"></span>督办记录</div>
            <a-table :dataSource="superviseList" :columns="superviseColumns" rowKey="id" :pagination="false" bordered />
        </div>

        <!-- 底部操作按钮 -->
        <div class="footer-actions">
            <a-button type="primary" @click="handleBack">返回列表</a-button>
        </div>
    </div>
</template>

<script>
import { mapGetters, mapMutations, mapState } from 'vuex'
import { fetchMeetingDetail, leaveConfirmMeeting, cancelMeeting } from '@/api/meeting'

export default {
    name: "MeetingDetailView",
    data() {
        return {
            filterType: 'all',
            detail: {},
            loading: false,

            // 人员列表列定义
            attendeeColumns: [
                { title: '姓名', dataIndex: 'userName', key: 'userName', scopedSlots: { customRender: 'nameSlot' } },
                { title: '部门', key: 'department', dataIndex: 'department' },
                { title: '角色', key: 'role', dataIndex: 'role' },
                { title: '响应状态', key: 'responseStatus', dataIndex: 'responseStatus', scopedSlots: { customRender: 'responseStatusSlot' } },
                { title: '到会状态', key: 'attendanceStatus', dataIndex: 'attendanceStatus', scopedSlots: { customRender: 'attendanceStatusSlot' } },
                {
                    title: '操作',
                    key: 'action',
                    align: 'center',
                    width: 160,
                    scopedSlots: { customRender: 'actionSlot' }
                },
            ],

            // 督办记录列定义
            superviseColumns: [
                { title: '督办事项', dataIndex: 'name', key: 'name' },
                { title: '办结时限', dataIndex: 'originalDate', key: 'originalDate' },
                { title: '督办类型', dataIndex: 'type', key: 'type' },
                { title: '执行人', dataIndex: 'assigneeName', key: 'assigneeName' },
                { title: '执行部门', dataIndex: 'assigneeDeptName', key: 'assigneeDeptName' },
                { title: '状态', dataIndex: '_statusText', key: 'status', align: 'center' },
                {
                    title: '操作', key: 'action', align: 'center',
                    render: () => <a style="color: #1890ff;">查看待办</a>
                },
            ],
        };
    },
    computed: {
        ...mapGetters('setting', ['findSingleDictName']),
        ...mapState('auth', ['user']),
        canOperate() {
            return !!(this.user && this.user.uuid && this.detail.applicantId === this.user.uuid);
        },
        attendeeList() {
            return (this.detail.attendeeList || []).map(item => {
                const respMap = { confirmed: '已确认', pending: '待响应', leave_approved: '已请假', leave_pending: '请假待审批', leave_applying: '请假申请' };
                const attMap = { present: '已到会', absent: '未到会', late: '迟到', leave_early: '早退' };
                const respClassMap = { confirmed: 'status-green', pending: 'status-yellow', leave_approved: 'status-gray', leave_pending: 'status-red', leave_applying: 'status-blue' };
                const attClassMap = { present: 'status-green', absent: 'status-red', late: 'status-yellow', leave_early: 'status-gray' };
                return {
                    ...item,
                    color: this.getAvatarColor(item.userName),
                    responseStatus: respMap[item.responseState] || item.responseState || '未响应',
                    responseClass: respClassMap[item.responseState] || 'status-yellow',
                    attendanceStatus: attMap[item.attendanceState] || item.attendanceState || '未开始',
                    attendanceClass: attClassMap[item.attendanceState] || 'status-yellow',
                    needApproval: item.responseState === 'leave_applying',
                    department: item.deptName || '',
                    role: item.role || '参会人',
                };
            });
        },
        fileList() {
            console.log("this.detail:::",this.detail)
            return (this.detail.minutesFiles || []).map(f => ({ ...f }));
        },
        attachFileList() {
            return (this.detail.attachFiles || []).map(f => ({ ...f }));
        },
        superviseList() {
            const statusMap = { finished: '已完成', pending: '待开始', processing: '进行中' };
            return (this.detail.superviseTasks || []).map(item => {
                const s = (item.status || '').toLowerCase();
                return { ...item, _statusText: statusMap[s] || item.status || '--' };
            });
        },
        filteredAttendeeList() {
            const list = this.attendeeList;
            if (this.filterType === 'all') return list;
            if (this.filterType === 'confirmed') return list.filter(a => a.responseClass === 'status-green');
            if (this.filterType === 'pending') return list.filter(a => a.responseClass === 'status-yellow');
            if (this.filterType === 'leave') return list.filter(a => a.responseClass === 'status-red' || a.responseClass === 'status-gray' || a.responseClass === 'status-blue');
            return list;
        },
        attendeeStats() {
            const list = this.attendeeList;
            return {
                confirmed: list.filter(a => a.responseClass === 'status-green').length,
                pending: list.filter(a => a.responseClass === 'status-yellow').length,
                leavePending: list.filter(a => a.responseClass === 'status-red' || a.responseClass === 'status-blue').length,
                leaveApproved: list.filter(a => a.responseClass === 'status-gray').length,
            };
        },
        hostInfo() {
            return this.attendeeList.find(a => a.role === '主持人') || {};
        },
        recorderInfo() {
            return this.attendeeList.find(a => a.role === '记录人') || {};
        },
        // 当前登录用户是否是本次会议的记录员
        currentUserIsRecorder() {
            const user = this.user || {};
            if (!user.uuid && !user.userName) return false;
            return this.attendeeList.some(item => {
                const itemUuid = item.uuid || item.userId || item.userUuid;
                // 优先 UUID 匹配（双方都非空才比较）
                if (itemUuid && user.uuid && itemUuid === user.uuid && item.role === '记录人') return true;
                // 次选 userName 匹配（双方都非空才比较）
                if (item.userName && user.userName && item.userName === user.userName && item.role === '记录人') return true;
                return false;
            });
        },
    },
    mounted() {
        const id = this.$route.query.id || this.$route.params.id;
        if (id) {
            this.fetchDetail(id);
        }
    },
    methods: {
        ...mapMutations('tabs', ['setTabList']),
        async fetchDetail(id) {
            this.loading = true;
            try {
                const res = await fetchMeetingDetail({ id });
                console.log("fetchMeetingDetail::::::::",res)
                this.detail = res;
            } catch (e) {
                console.error('获取会议详情失败', e);
            } finally {
                this.loading = false;
            }
        },
        formatDate(dateStr) {
            if (!dateStr) return '';
            const d = new Date(dateStr);
            const pad = n => String(n).padStart(2, '0');
            return `${d.getFullYear()}年${pad(d.getMonth() + 1)}月${pad(d.getDate())}日 ${pad(d.getHours())}:${pad(d.getMinutes())}`;
        },
        getAvatarColor(name) {
            if (!name) return '#1890ff';
            const colors = ['#1890ff', '#52c41a', '#722ed1', '#eb2f96', '#faad14', '#13c2c2', '#f5222d', '#2f54eb'];
            let hash = 0;
            for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
            return colors[Math.abs(hash) % colors.length];
        },
        fileIcon(ext) {
            if (!ext) return 'file';
            const map = { pdf: 'file-pdf', doc: 'file-word', docx: 'file-word', xls: 'file-excel', xlsx: 'file-excel', ppt: 'file-ppt', pptx: 'file-ppt', txt: 'file-text', jpg: 'file-image', jpeg: 'file-image', png: 'file-image', gif: 'file-image', bmp: 'file-image' };
            return map[ext.toLowerCase()] || 'file';
        },
        handleBack() {
            this.$router.go(-1);
        },
        // 打开编辑页（修改时间/会议信息）
        handleOpenEdit() {
            const path = `/oa/meeting/edit?id=${this.detail.id}`;
            const basePath = `/oa/meeting/edit`;
            const tabList = [...this.$store.state.tabs.tabList];
            if (!tabList.some(t => t.path === basePath)) {
                tabList.push({ name: '编辑会议', path: basePath, fullPath: path });
                this.setTabList(tabList);
            }
            this.$router.push(path).catch(() => {});
        },
        // 取消会议
        handleCancelMeeting() {
            this.$confirm({
                title: '确定取消该会议吗?',
                content: '取消后会议将无法恢复',
                onOk: () => this.confirmCancel()
            });
        },
        confirmCancel() {
            return cancelMeeting({ id: this.detail.id }).then(() => {
                this.$message.success('会议已取消');
                this.fetchDetail(this.detail.id);
            });
        },
        downloadFile(file) {
            if (!file.completePath) return;
            const a = document.createElement('a');
            a.href = file.completePath;
            a.download = file.name || 'download';
            a.target = '_blank';
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
        },
        // 批准请假
        handleApproveLeave(record) {
            this.$confirm({
                title: '确定批准该请假申请吗?',
                content: '批准后将视为该参会人已请假',
                onOk: () => this.confirmLeave(record, 'agree')
            });
        },
        // 驳回请假
        handleRejectLeave(record) {
            this.$confirm({
                title: '确定驳回该请假申请吗?',
                content: '驳回后该参会人需要重新响应',
                onOk: () => this.confirmLeave(record, 'reject')
            });
        },
        confirmLeave(record, confirm) {
            leaveConfirmMeeting({
                attendeeId: record.id,
                bookInfoId: this.detail.id,
                confirm
            }).then(() => {
                this.$message.success(confirm === 'agree' ? '已同意请假' : '已拒绝请假');
                this.fetchDetail(this.detail.id);
            });
        }
    }
};
</script>

<style lang="less" scoped>
.meeting-detail-view {
    background: #f5f7fa;
    padding: 24px;
    min-height: 100vh;
}

.card-block {
    background: #fff;
    border-radius: 8px;
    padding: 20px 24px;
    margin-bottom: 20px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.02);
}

/* 标题和蓝条 */
.section-title {
    font-size: 16px;
    font-weight: 600;
    color: #1f1f1f;
    display: flex;
    align-items: center;
    margin-bottom: 16px;
    .bar {
        display: inline-block;
        width: 4px;
        height: 16px;
        background: #1890ff;
        border-radius: 2px;
        margin-right: 8px;
    }
}

/* 顶部区 */
.header-card {
    display: flex;
    align-items: flex-start;
    gap: 24px;

    .header-top {
        display: flex;
        align-items: center;
        margin-bottom: 16px;
        .title-section {
            display: flex;
            align-items: center;
            flex-wrap: wrap;
            gap: 12px;
            .main-title {
                font-size: 20px;
                font-weight: 600;
                color: #1f1f1f;
            }
            .custom-tag {
                border: none; padding: 2px 12px; border-radius: 12px;
                &.gray-tag { background: #f5f5f5; color: #8c8c8c; }
            }
        }
    }

    .title-actions {
        margin-left: auto;
        display: flex;
        gap: 8px;
        flex-shrink: 0;
    }

    .header-info-row {
        display: flex;
        flex-wrap: wrap;
        gap: 24px;
        color: #595959;
        font-size: 14px;

        .info-item {
            display: flex;
            align-items: center;
            gap: 6px;

            &.person-item {
                margin-left: 8px;
                .avatar-circle {
                    width: 32px; height: 32px; border-radius: 50%;
                    color: #fff; display: flex; align-items: center; justify-content: center;
                    font-size: 12px; margin-right: 8px;
                }
                .person-detail {
                    .person-role { font-size: 12px; color: #999; line-height: 1.2; }
                    .person-name {
                        font-weight: 500; color: #333;
                        .sub-text { font-weight: normal; font-size: 12px; color: #999; }
                    }
                    .person-sub {
                        display: flex; align-items: center; gap: 8px; margin-top: 4px;
                        font-size: 12px; color: #999;
                        .status-badge {
                            display: inline-block; padding: 0 8px; border-radius: 10px; font-size: 11px;
                            &.status-green { background: #f6ffed; color: #52c41a; }
                            &.status-yellow { background: #fffbe6; color: #faad14; }
                            &.status-red { background: #fff1f0; color: #ff4d4f; }
                            &.status-gray { background: #f5f5f5; color: #8c8c8c; }
                            &.status-blue { background: #e6f7ff; color: #1890ff; }
                        }
                    }
                }
            }
        }
    }
}

/* 会议内容 */
.meeting-content-list {
    .content-item {
        padding: 6px 0;
        color: #333;
        font-size: 14px;
    }
}

/* 参会响应状态 */
.stats-grid {
    display: flex;
    gap: 16px;
    margin-bottom: 16px;

    .stat-card {
        flex: 1;
        padding: 16px 20px;
        border-radius: 6px;
        .stat-num { font-size: 24px; font-weight: bold; margin-bottom: 4px; }
        .stat-label { font-size: 14px; color: #8c8c8c; }

        &.stat-green { background: #f6ffed; .stat-num { color: #52c41a; } }
        &.stat-yellow { background: #fffbe6; .stat-num { color: #faad14; } }
        &.stat-red { background: #fff1f0; .stat-num { color: #ff4d4f; } }
        &.stat-gray { background: #fafafa; .stat-num { color: #8c8c8c; } }
    }
}

.filter-tabs {
    margin-bottom: 16px;
    display: flex;
    gap: 8px;
}

.attendee-list-table {
    .user-cell {
        display: flex; align-items: center; gap: 8px;
        .avatar-circle.small { width: 24px; height: 24px; border-radius: 50%; color: #fff; display: flex; justify-content: center; align-items: center; font-size: 11px; }
    }
    .status-badge {
        display: inline-block; padding: 2px 12px; border-radius: 12px; font-size: 12px;
        &.status-green { background: #f6ffed; color: #52c41a; }
        &.status-yellow { background: #fffbe6; color: #faad14; }
        &.status-red { background: #fff1f0; color: #ff4d4f; }
        &.status-gray { background: #f5f5f5; color: #8c8c8c; }
        &.status-blue { background: #e6f7ff; color: #1890ff; }
    }

    .action-group {
        display: flex;
        justify-content: center;
        gap: 8px;
        .btn-approve {
            background: #52c41a;
            border-color: #52c41a;
        }
        .btn-reject {
            border: 1px solid #d9d9d9;
            color: #595959;
            &:hover { color: #1890ff; border-color: #1890ff; }
        }
    }
    .empty-action { color: #d9d9d9; }
}

/* 会议纪要 */
.file-list {
    display: flex; flex-direction: column; gap: 12px;
    .file-item {
        display: flex; justify-content: space-between; align-items: center;
        background: #fafafa; border: 1px solid #f0f0f0; border-radius: 4px; padding: 10px 16px;
        .file-info {
            display: flex; align-items: center; gap: 12px;
            .file-icon { font-size: 20px; color: #1890ff; }
            .file-name { color: #333; font-weight: 500; }
        }
        .file-actions {
            display: flex; align-items: center; gap: 16px;
            .file-size { color: #999; font-size: 12px; }
            .download-icon { color: #1890ff; font-size: 16px; cursor: pointer; }
        }
    }
}

/* 底部 */
.footer-actions {
    display: flex; justify-content: center; padding: 20px 0 40px;
}
</style>